/* /admin/payments — receipt verification queue (prototype billing.js pgAdminPayments + verifyModal). */
import { useState } from "react";
import { useAdminPayments } from "../../data/hooks";
import type { Payment } from "../../data/types";
import { useI18n } from "../../i18n";
import { gDate } from "../../lib/format";
import { Chip, EmptyState, ErrorBox, Icon, IconButton, Money, PageHeader, Skeleton, Tabs } from "../../ui";
import { VerifyModal, looksGood } from "./components/VerifyModal";

type Tab = "Pending" | "Verified" | "Rejected" | "all";

export default function AdminPayments() {
  const { t, L, lang } = useI18n();
  const [tab, setTab] = useState<Tab>("Pending");
  const pendingQ = useAdminPayments("Pending");
  const q = useAdminPayments(tab === "all" ? undefined : tab);
  const [openId, setOpenId] = useState<string | null>(null);
  const open: Payment | undefined = q.data?.find((p) => p.id === openId) ?? pendingQ.data?.find((p) => p.id === openId);
  return (
    <div data-testid="admin-payments">
      <PageHeader title={L("Payment verification", "التحقق من المدفوعات")} sub={L("Match each receipt with the bank statement, then verify to activate the subscription.", "طابق كل إيصال مع كشف الحساب ثم فعّل الاشتراك.")} />
      <section className="card">
        <Tabs label={L("Payment status", "حالة الدفع")} value={tab} onChange={setTab}
          items={[["Pending", t("Pending"), pendingQ.data?.length ?? null], ["Verified", L("Verified", "تم التحقق")], ["Rejected", t("Rejected")], ["all", t("All")]]} />
        {q.error ? <div className="card-b"><ErrorBox error={q.error} onRetry={() => q.refetch()} /></div> : !q.data ? <div className="card-b"><Skeleton /></div> : q.data.length === 0 ? (
          <EmptyState title={L("Nothing here", "لا يوجد شيء")} desc={L("New receipts appear as soon as customers upload them.", "تظهر الإيصالات الجديدة فور رفعها.")} />
        ) : (
          <div className="tw">
            <table className="tbl cards" data-testid="admin-payments-table">
              <thead><tr><th>{L("Customer", "العميل")}</th><th>{L("Reference", "المرجع")}</th><th className="end">{L("Expected", "المتوقع")}</th><th className="end">{L("Transferred", "المحوّل")}</th><th className="hide-md">{L("AI check", "فحص الذكاء")}</th><th>{t("Status")}</th><th className="end" aria-label={t("Actions")} /></tr></thead>
              <tbody>
                {q.data.map((p) => {
                  const ok = looksGood(p);
                  return (
                    <tr key={p.id} className="click" onClick={() => setOpenId(p.id)} data-testid={"pay-row-" + p.id}>
                      <td className="k1"><b>{p.tenant}</b><div className="xs muted">{p.plan} · {p.seats} · {gDate(p.date, lang)}</div></td>
                      <td className="x num sm" data-l={L("Reference", "المرجع")}>{p.ref}</td>
                      <td className="end x" data-l={L("Expected", "المتوقع")}><Money value={p.total} /></td>
                      <td className="end k3" data-l={L("Transferred", "المحوّل")}><Money value={p.paid} /></td>
                      <td className="hide-md">{p.status === "Pending" ? (ok ? <span className="chip ok"><Icon name="sparkle" className="s" />{L("Matches", "مطابق")}</span> : <span className="chip warn"><Icon name="alert" className="s" />{L("Check", "تحقق")}</span>) : "—"}</td>
                      <td className="k2"><Chip status={p.status === "Verified" ? "Approved" : p.status} /></td>
                      <td className="end x" data-l=""><IconButton icon="chevron-right" label={L("Review ", "مراجعة ") + p.id} onClick={(e) => { e.stopPropagation(); setOpenId(p.id); }} data-testid={"btn-open-" + p.id} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {open && <VerifyModal p={open} onClose={() => setOpenId(null)} />}
    </div>
  );
}
